import React from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Box, Button, Card, CardContent, Snackbar, Stack, TextField, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  getWoymTitle,
  updateWoymTitle,
} from "../../../../service/BuyerConfiguration/BuyerAppDashboard/homeTabsService";

const GREEN = "#00B14F";

/**
 * Top of Dish Management: back to Buyer App Dashboard, and the editable
 * heading the buyer app shows above these categories.
 */
const WoymTitleEditor: React.FC = () => {
  const navigate = useNavigate();
  const [saved, setSaved] = React.useState("");
  const [title, setTitle] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [toast, setToast] = React.useState<{ msg: string; ok: boolean } | null>(null);

  React.useEffect(() => {
    getWoymTitle()
      .then((t) => {
        setSaved(t);
        setTitle(t);
      })
      .catch(() => {
        setSaved("What's on your mind?");
        setTitle("What's on your mind?");
      });
  }, []);

  const save = async () => {
    const clean = title.trim();
    if (!clean) return;
    setSaving(true);
    try {
      await updateWoymTitle(clean);
      setSaved(clean);
      setToast({ msg: "Title saved. Open apps update in a few seconds.", ok: true });
    } catch (e: any) {
      setToast({ msg: e?.response?.data?.message || "Couldn't save the title. Try again.", ok: false });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/buyer-app-dashboard")}
        sx={{ textTransform: "none", color: GREEN, mb: 1, px: 0 }}
      >
        Back to Buyer App Dashboard
      </Button>
      <Card sx={{ borderRadius: 3, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
        <CardContent>
          <Typography sx={{ fontWeight: 700, fontSize: 16 }}>Section title</Typography>
          <Typography sx={{ color: "text.secondary", fontSize: 13, mt: 0.5, mb: 1.5 }}>
            The heading the buyer app shows above these categories.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
            <TextField
              size="small"
              fullWidth
              value={title}
              inputProps={{ maxLength: 60 }}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What's on your mind?"
              sx={{ maxWidth: 420 }}
            />
            <Button
              variant="contained"
              disabled={saving || !title.trim() || title.trim() === saved}
              onClick={save}
              sx={{ bgcolor: GREEN, "&:hover": { bgcolor: "#0a7a3c" }, textTransform: "none", whiteSpace: "nowrap" }}
            >
              {saving ? "Saving…" : "Save title"}
            </Button>
          </Stack>
        </CardContent>
      </Card>
      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? (
          <Alert severity={toast.ok ? "success" : "error"} onClose={() => setToast(null)}>
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default WoymTitleEditor;
